/**
 * SchoolGardenMap — public, anonymized community map of all school gardens.
 *
 * Route: /schulgaerten (PUBLIC, no auth)
 *
 * Privacy posture: centroids are pre-rounded by the backend to 1 decimal
 * (~11 km), and the original polygons are never exposed. Only the
 * teacher-set `school_label` (e.g. "Klasse 7b HAK Linz") is shown on the
 * marker pop-up.
 */
import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { MapPin, ArrowLeft, Leaf, Tree, GraduationCap, Sparkle, ImagesSquare } from "@phosphor-icons/react";
import { fetchPublicSchoolGardens, fetchPublicSchoolGardenPhotos, schoolGardenPhotoUrl } from "@/lib/api";

const ANCHOR = [50.3, 11.5];   // Centred on DE/AT

export default function SchoolGardenMap() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [photos, setPhotos] = useState([]);
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersLayer = useRef(null);

  useEffect(() => {
    fetchPublicSchoolGardens()
      .then(setData)
      .catch((e) => setError(e?.message || "fetch failed"));
    fetchPublicSchoolGardenPhotos(12)
      .then((r) => setPhotos(r.photos || []))
      .catch(() => setPhotos([]));
  }, []);

  // Map init once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    if (containerRef.current._leaflet_id) delete containerRef.current._leaflet_id;
    const map = L.map(containerRef.current, {
      center: ANCHOR, zoom: 6, scrollWheelZoom: true,
    });
    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
      attribution: "© OpenStreetMap",
    }).addTo(map);
    markersLayer.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => { try { map.remove(); } catch { /* */ } mapRef.current = null; };
  }, []);

  // Plot markers when data arrives.
  useEffect(() => {
    if (!data?.gardens || !markersLayer.current) return;
    markersLayer.current.clearLayers();
    data.gardens.forEach((g) => {
      const icon = L.divIcon({
        className: "school-garden-marker",
        html: `<div style="background:#00FA9A;color:#0A0A0A;border-radius:50%;width:20px;height:20px;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.25);font-size:11px;font-weight:bold;">🌱</div>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });
      const m = L.marker([g.lat, g.lon], { icon });
      m.bindPopup(
        `<div style="font-family:sans-serif;min-width:160px;">
          <div style="font-weight:600;color:#00FA9A;font-size:13px;">${escapeHtml(g.school_label)}</div>
          <div style="font-size:12px;color:#1A1A1A;margin-top:3px;">${escapeHtml(g.label || "Schulgarten")}</div>
          <div style="font-size:10px;color:#8A8A8A;margin-top:4px;font-family:monospace;">${Math.round(g.area_m2 || 0)} m² · seit ${g.joined_at}</div>
        </div>`,
        { closeButton: true }
      );
      m.addTo(markersLayer.current);
    });
  }, [data]);

  return (
    <>
      <Helmet>
        <title>Schulgärten Mitteleuropa · Bodenblick</title>
        <meta
          name="description"
          content="Anonymisierte Karte aller Schulgärten in Deutschland und Österreich, die mit Bodenblick beobachten. Wetter, Boden und Bienen — gemeinsam lernen."
        />
      </Helmet>
      <div className="min-h-screen bg-[#0A0A0A]" data-testid="school-gardens-public">
        <header className="border-b border-white/15 bg-[#121212]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-[#00FA9A] hover:underline" data-testid="schoolgardens-back">
              <ArrowLeft size={14} /> <Leaf size={14} weight="fill" /> Bodenblick
            </Link>
            <Link to="/school" className="text-xs text-white/60 hover:text-[#00FA9A] inline-flex items-center gap-1">
              <GraduationCap size={12} /> Schul-Code eingeben
            </Link>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="mb-10 max-w-3xl">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#00FA9A] font-mono mb-3 inline-flex items-center gap-2">
              <Sparkle size={12} weight="fill" /> Schulen · Community · Bodenblick
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-[5rem] font-bold text-white tracking-tighter leading-[0.95] bb-accent-stripe">
              Schulgärten Mitteleuropa.
            </h1>
            <p className="mt-5 text-base text-white/70 leading-relaxed">
              {data?.count ? (
                <>
                  <strong className="text-[#00FA9A]" data-testid="school-gardens-count">{data.count}</strong>{" "}
                  Schulen{" "}beobachten gerade Wetter, Boden und Bestäuber auf ihrem eigenen
                  Schulgarten — gemeinsam, anonymisiert, ohne Werbung.
                </>
              ) : data ? (
                "Noch keine Schule eingetragen. Sei die erste — euer Lehrer:in besorgt sich einen Schul-Code von Bodenblick."
              ) : (
                "Lade Schulgärten…"
              )}
            </p>
            <div className="mt-4 text-[12px] text-white/60 inline-flex items-center gap-1.5">
              <MapPin size={12} className="text-zinc-400" />
              Marker-Positionen sind auf ~10 km gerundet, exakte Polygone bleiben privat.
            </div>
          </div>

          {error ? (
            <div className="eid-card p-6 text-sm text-zinc-400">
              Karte derzeit nicht erreichbar — wir versuchen es gleich erneut.
            </div>
          ) : (
            <div className="border border-white/15 bg-[#121212] overflow-hidden" data-testid="school-gardens-map">
              <div ref={containerRef} className="w-full h-[520px]" />
            </div>
          )}

          <div className="mt-12 grid sm:grid-cols-3 gap-5 text-sm">
            <div className="border-l-2 border-[#00FA9A]/40 pl-4">
              <Tree size={20} weight="fill" className="text-[#00FA9A]" />
              <h3 className="font-bold text-white mt-2">Mitmachen</h3>
              <p className="text-white/70 leading-relaxed mt-1">
                Eure Schule bekommt von Bodenblick einen Schul-Code (Lehrer:in
                schreibt uns an). Mit dem Code zeichnet ihr euren Garten.
              </p>
            </div>
            <div className="border-l-2 border-[#FFC24B]/40 pl-4">
              <Sparkle size={20} weight="fill" className="text-[#FFC24B]" />
              <h3 className="font-bold text-white mt-2">Beobachten</h3>
              <p className="text-white/70 leading-relaxed mt-1">
                Täglich: Wetter, Bodenfeuchte, Bienenflug, Empfehlung „heute
                ein guter Tag für…". Kid-friendly Datenkarte.
              </p>
            </div>
            <div className="border-l-2 border-[#D9A87C]/40 pl-4">
              <GraduationCap size={20} weight="fill" className="text-[#D9A87C]" />
              <h3 className="font-bold text-white mt-2">Lernen</h3>
              <p className="text-white/70 leading-relaxed mt-1">
                Boden lebt, Wetter wandelt sich, Bestäuber zählen. Echte Daten
                statt Lehrbuch-Theorie — direkt vor der Schultür.
              </p>
            </div>
          </div>

          {photos.length > 0 && (
            <section className="mt-16" data-testid="public-photo-gallery">
              <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#00FA9A] font-mono inline-flex items-center gap-2">
                    <ImagesSquare size={12} weight="fill" /> Foto-Tagebuch · jüngste {photos.length}
                  </p>
                  <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter mt-1.5">
                    Aus den Schulgärten — heute.
                  </h2>
                </div>
                <span className="text-[11px] font-mono text-white/50 max-w-xs">
                  Schul-Label sichtbar · Standort auf ~110 km abstrahiert · keine Geräte- oder EXIF-Daten
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {photos.map((p) => (
                  <figure
                    key={p.id}
                    className="border border-white/15 bg-[#121212] overflow-hidden group"
                    data-testid={`public-photo-${p.id}`}
                  >
                    <div className="aspect-square bg-white/5 overflow-hidden">
                      <img
                        src={schoolGardenPhotoUrl(p.id)}
                        alt={p.caption || p.school_label}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <figcaption className="p-2.5">
                      <div className="text-[10px] uppercase tracking-widest font-mono text-[#00FA9A]">
                        {p.school_label}
                      </div>
                      {p.caption && (
                        <p className="text-xs text-white mt-1 leading-snug line-clamp-2">
                          {p.caption}
                        </p>
                      )}
                      {p.photo_date && (
                        <p className="text-[10px] font-mono text-white/40 mt-1">
                          {p.photo_date}
                        </p>
                      )}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </section>
          )}
        </main>
      </div>
    </>
  );
}

function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));
}
